/**
 * Every message the DBS validation rules can produce, keyed by rule id (DBS-344). This is the only place a message
 * is written: the rules, the portals, the e2e specs and the screenshot generators all import from here, so a wording
 * change moves everything together.
 *
 * Parameterised messages are functions; the rule supplies the values.
 */

const documentDateOfBirthMismatch = (document: 'passport' | 'driver licence', documentDob: string, dateOfBirth: string): string =>
  `The date of birth on your ${document} (${documentDob.substring(0, 10)}) does not match the date of birth you gave in the Birth section (${dateOfBirth.substring(0, 10)}). One of them is wrong; please correct whichever it is.`

export const MESSAGES = {
  // XSD field constraints
  'field.required': 'Required',
  'field.pattern': (label: string) => `Invalid ${label}`,
  'field.tooLong': (label: string, max: number, actual: number) => `Too long: the DBS allows ${max} characters for the ${label} and this is ${actual}`,
  'field.tooShort': (label: string, min: number) => `Too short: the DBS needs at least ${min} characters for the ${label}`,
  'field.length': (label: string, length: number) => `The ${label} must be exactly ${length} characters`,
  'field.enum': (label: string) => `The ${label} is not one of the values the DBS accepts`,
  'field.tooMany': (label: string, max: number) => `The DBS allows at most ${max} ${label}s`,
  'text.upperCase': 'Must be in upper case: the DBS does not accept lower-case letters',

  // Dates
  'date.before1900': 'Before 1900',
  'date.future': 'Future Date',
  'date.beforeBirth': 'Before Born',
  'dob.under16': 'Under 16',
  'dob.over110': 'Over 110',

  // Identity
  'ni.format': 'Invalid National Insurance number.',
  'ni.prefix': 'National Insurance numbers cannot start with: BG, GB, KN, NK, NT, TN, or ZZ',
  'contact.format': 'Must start with 0, followed by 1-9, followed by 8 to 10 digits',
  'language.englishOnly': 'Online applications are only available in English at this time. Please select English.',
  'title.gender': (title: string, gender: string) => `The title "${title}" cannot be used with the "${gender}" gender`,

  // Names
  'names.birthSurname.title': (title: string) => `The title ${title} requires a Birth Surname`,
  'names.birthSurname.female': 'Females not titled MISS require a Birth Surname',
  'names.otherName.order': 'Not Before From',
  'names.warning': (message: string) => message,

  // Addresses
  'address.postcode.required': 'UK addresses must have a valid postcode',
  'address.postcode.format': 'Invalid Postcode.',
  'address.previous.order': 'Must be before Resident To Date',
  'address.gaps': 'There are gaps in your address history.',
  'address.fiveYears': (yearsCovered: number) => `Needs Additional Address History: ${yearsCovered} of the required 5 years covered`,
  'address.nfa.notGB': 'Must not be in the UK',

  // Text content
  'text.placeholder': (placeholder: string) => `Please leave this field blank if it does not apply, rather than entering "${placeholder}".`,
  'county.notCountry.uk': (typed: string) => `"${typed}" is a country, not a county. Enter the county (for example Lancashire), or leave this blank.`,
  'county.notCountry.repeat': (typed: string) => `"${typed}" is the country, which you have already chosen. Enter the county, state or region, or leave this blank.`,

  // Documents
  'documents.licence.number': 'Invalid Driver Licence Number.',
  'documents.dob.passport': (documentDob: string, dateOfBirth: string) => documentDateOfBirthMismatch('passport', documentDob, dateOfBirth),
  'documents.dob.licence': (documentDob: string, dateOfBirth: string) => documentDateOfBirthMismatch('driver licence', documentDob, dateOfBirth),

  // Declaration
  'declaration.applicant': 'You must declare you have been truthful',
  'declaration.unspentConvictions': 'Please answer whether you have any unspent convictions',

  // Registered Body side (countersigning and the batch)
  'identity.verified': 'The applicant\'s identity has not been verified',
  'rb.number': 'The Registered Body number must be 11 digits',
  'rb.csNumber': 'The countersignatory number must be 11 digits',
  'rb.addressChecked': 'The current address details have not been checked',

  // Job role
  'job.position': (reason: string) => reason,
  'job.enhancedRequired.children': 'Working with Children requires Enhanced application type',
  'job.enhancedRequired.adults': 'Working with Adults requires Enhanced application type',
  'job.enhancedRequired.home': 'Working at Home Address requires Enhanced application type',
} as const

export type RuleId = keyof typeof MESSAGES

/** The message for a rule, with its parameters when it has any */
export const message = <K extends RuleId>(id: K, ...params: typeof MESSAGES[K] extends (...args: infer A) => string ? A : []): string => {
  const entry: string | ((...args: any[]) => string) = MESSAGES[id]
  return typeof entry === 'function' ? entry(...params) : entry
}
